import { useRef, useState } from "react";
import axios from "axios";
import { markOrdersPaid } from "./bulkOrderPayment";
import { sortOrdersByDeparture } from "../../utils/orderDepartureSort";
import { matchesServiceDateRange } from '../../utils/orderServiceDateRange';
import { getAdminOrderTimeCategory } from "../../utils/adminOrderTimeCategory";
import { DriverCredentialVault } from "../../services/driverApp/credentialPlugin";
import {
  containsPaidAdminOrder,
  promptForPaidOrderDeletion,
} from "../../components/admin/adminOrderDeleteConfirmation";

const CLOSED_ORDER_STATUSES = new Set(["completed", "cancelled", "canceled", "deleted", "refunded"]);

const getCurrentMonthValue = () => {
  const now = new Date();
  const year = now.getFullYear();
  const month = String(now.getMonth() + 1).padStart(2, "0");
  return `${year}-${month}`;
};

const getInitialMonthFilter = () => {
  if (typeof window === "undefined" || !window.matchMedia) {
    return "";
  }
  return window.matchMedia("(max-width: 640px)").matches ? getCurrentMonthValue() : "";
};

function mergeSearchResultsWithLoadedOrders(searchOrders = [], loadedOrders = []) {
  const loadedOrdersById = new Map();
  const loadedOrdersByOrderNo = new Map();
  loadedOrders.forEach((order) => {
    if (order?.id) loadedOrdersById.set(order.id, order);
    if (order?.order_no) loadedOrdersByOrderNo.set(order.order_no, order);
  });
  return searchOrders.map((order) =>
    loadedOrdersById.get(order?.id) ||
    loadedOrdersByOrderNo.get(order?.order_no) ||
    order
  );
}

function parseClockMinutes(value) {
  const [hours, minutes] = String(value || "").split(":").map(Number);
  if (!Number.isFinite(hours) || !Number.isFinite(minutes)) {
    return null;
  }
  return hours * 60 + minutes;
}

export function getCharterTimeReportStatus(order) {
  if (order?.service_type !== "charter" || !order?.reported_end_time || !order?.pickup_time || !order?.selected_hours) {
    return null;
  }

  const startMinutes = parseClockMinutes(order.pickup_time);
  let endMinutes = parseClockMinutes(order.reported_end_time);
  const bookedHours = Number(order.selected_hours);

  if (startMinutes === null || endMinutes === null || !Number.isFinite(bookedHours)) {
    return null;
  }

  if (endMinutes < startMinutes) {
    endMinutes += 24 * 60;
  }

  const actualHours = (endMinutes - startMinutes) / 60;
  const diff = actualHours - bookedHours;

  if (diff > 0) {
    const isHandled = order.overtime_handled === true;
    return {
      type: "overtime",
      diff,
      actual: actualHours,
      text: `超时${diff}小时，共计${actualHours}小时`,
      isHandled,
      color: isHandled ? "text-red-600" : "text-red-600 font-bold"
    };
  }

  if (diff < 0) {
    return {
      type: "early",
      diff: Math.abs(diff),
      actual: actualHours,
      text: `提前结束，共计${actualHours}小时`,
      isHandled: true,
      color: "text-green-600"
    };
  }

  return {
    type: "ontime",
    diff: 0,
    actual: actualHours,
    text: "准时结束",
    isHandled: true,
    color: "text-blue-600"
  };
}

function formatBookingPaymentLinkMessage(paymentUrl) {
  return [
    `Please find below the booking confirmation for your upcoming service: ${paymentUrl}`,
    "",
    "Kindly review the details, and if everything is correct, please proceed with the payment through the link above.",
    "",
    "Thank you very much for your booking.",
    "",
    "Best regards,",
    "",
    "Shonan Internat",
    "Tony",
  ].join("\n");
}

export async function copyTextToClipboard(text) {
  if (!text) return false;

  if (DriverCredentialVault?.writeClipboard) {
    try {
      const result = await DriverCredentialVault.writeClipboard({ text });
      if (result?.copied === true) {
        return true;
      }
    } catch {
      // Fall back to browser copy paths for web and older app builds.
    }
  }

  if (navigator.clipboard?.writeText) {
    try {
      await navigator.clipboard.writeText(text);
      return true;
    } catch {
      // iOS WebView often rejects the async clipboard API after a network request.
    }
  }

  if (typeof document === "undefined") return false;

  const textarea = document.createElement("textarea");
  textarea.value = text;
  textarea.setAttribute("readonly", "");
  textarea.style.position = "fixed";
  textarea.style.top = "0";
  textarea.style.left = "0";
  textarea.style.width = "1px";
  textarea.style.height = "1px";
  textarea.style.opacity = "0";
  textarea.style.pointerEvents = "none";

  document.body.appendChild(textarea);
  textarea.focus();
  textarea.select();
  textarea.setSelectionRange(0, textarea.value.length);

  let copied = false;
  try {
    copied = document.execCommand("copy");
  } catch {
    copied = false;
  } finally {
    document.body.removeChild(textarea);
  }

  return copied;
}

export default function useAdminOrders({ apiBaseUrl, orders, fetchOrders, getErrorMessage, refreshStats, setLoading, notifyAdminApp }) {
  const [orderStatusFilter, setOrderStatusFilter] = useState("all");
  const [customerFilter, setCustomerFilter] = useState("");
  const [monthFilter, setMonthFilter] = useState(() => getInitialMonthFilter());
  const [serviceDateRange, setServiceDateRange] = useState({ from: '', to: '' });
  const [unlinkedCustomerOnly, setUnlinkedCustomerOnly] = useState(false);
  const [sortByDate, setSortByDate] = useState(true);
  const [dateSortDirection, setDateSortDirection] = useState("asc");
  const [hideCompleted, setHideCompleted] = useState(false);
  const [hideCancelled, setHideCancelled] = useState(false);

  const [searchKeyword, setSearchKeyword] = useState("");
  const [searchResults, setSearchResults] = useState([]);
  const [isSearching, setIsSearching] = useState(false);
  const [searchMessage, setSearchMessage] = useState("");
  const [isSearchMode, setIsSearchMode] = useState(false);
  const activeSearchKeyword = useRef("");
  const searchRequest = useRef(0);

  const [selectedOrderIds, setSelectedOrderIds] = useState([]);
  const [selectAll, setSelectAll] = useState(false);
  const markingPaid = useRef(false);
  const [isMarkingPaid, setIsMarkingPaid] = useState(false);
  const [paymentCopyPanel, setPaymentCopyPanel] = useState(null);
  const showPaymentNotice = (message) => {
    if (typeof notifyAdminApp === "function") {
      notifyAdminApp(message);
      return;
    }
    alert(message);
  };
  const refreshOrdersAndStats = () => {
    fetchOrders();
    refreshStats?.();
  };

  const getOrderTimeCategory = (order) => {
    return getAdminOrderTimeCategory(order);
  };

  const hasValue = (value) => String(value || "").trim().length > 0;

  const isUnlinkedCustomerOrder = (order) =>
    !hasValue(order.user_id) &&
    !hasValue(order.user_email) &&
    !hasValue(order.customer_id);

  const isDriverOvertimeUnresolvedOrder = (order) => {
    const status = getCharterTimeReportStatus(order);
    return status?.type === "overtime" && order.overtime_handled !== true;
  };

  const isMissingDriverOrder = (order) => {
    const status = String(order?.status || "").toLowerCase();
    if (CLOSED_ORDER_STATUSES.has(status)) return false;
    return !order?.assigned_driver_id && !order?.assigned_driver?.driver_account_id && !order?.assigned_driver?.driver_name;
  };

  const getFilteredOrders = () => {
    let filtered = [...orders];

    if (hideCompleted && !["driver_overtime_unhandled", "unresolved_driver_issues"].includes(orderStatusFilter)) {
      filtered = filtered.filter(order => getOrderTimeCategory(order) !== "completed");
    }

    if (hideCancelled) {
      filtered = filtered.filter(order => order.status !== "cancelled");
    }

    if (orderStatusFilter === "unresolved_driver_issues") {
      filtered = filtered.filter(order => order.driver_issue_unresolved === true || isDriverOvertimeUnresolvedOrder(order));
    } else if (orderStatusFilter === "flight_audit_errors") {
      filtered = filtered.filter(order => order.flight_audit_error === true || order.flight_audit_status === "error");
    } else if (orderStatusFilter === "missing_driver") {
      filtered = filtered.filter(isMissingDriverOrder);
    } else if (orderStatusFilter === "driver_overtime_unhandled") {
      filtered = filtered.filter(isDriverOvertimeUnresolvedOrder);
    } else if (!hideCompleted && orderStatusFilter !== "all") {
      filtered = filtered.filter(order => getOrderTimeCategory(order) === orderStatusFilter);
    }

    if (monthFilter) {
      filtered = filtered.filter(order =>
        order.departure_date && order.departure_date.startsWith(monthFilter)
      );
    }

    filtered = filtered.filter(order => matchesServiceDateRange(order, serviceDateRange));

    if (customerFilter.trim()) {
      filtered = filtered.filter(order =>
        order.user_email && order.user_email === customerFilter
      );
    }

    if (unlinkedCustomerOnly) {
      filtered = filtered.filter(isUnlinkedCustomerOrder);
    }

    if (sortByDate) {
      filtered = sortOrdersByDeparture(filtered, dateSortDirection);
    } else {
      filtered.sort((a, b) => {
        const dateA = new Date(a.created_at);
        const dateB = new Date(b.created_at);
        return dateB - dateA;
      });
    }

    return filtered;
  };

  const getUniqueCustomers = () => {
    const customers = new Map();
    orders.forEach(order => {
      if (order.user_email && !customers.has(order.user_email)) {
        customers.set(order.user_email, {
          email: order.user_email,
          name: order.customer_name
        });
      }
    });
    return Array.from(customers.values()).sort((a, b) =>
      (a.email || "").localeCompare(b.email || "")
    );
  };

  const handleSearchOrders = async (requestedKeyword, loadedOrders = orders) => {
    const request = ++searchRequest.current;
    const keyword = typeof requestedKeyword === "string" ? requestedKeyword.trim() : searchKeyword.trim();
    activeSearchKeyword.current = keyword;
    if (!keyword) {
      setIsSearching(false);
      setIsSearchMode(false);
      setSearchResults([]);
      setSearchMessage("");
      return;
    }

    setIsSearching(true);
    setSearchMessage("");

    try {
      const token = localStorage.getItem("token");
      const response = await axios.get(`${apiBaseUrl}/admin/orders/search`, {
        params: { q: keyword },
        headers: { Authorization: `Bearer ${token}` }
      });

      if (request !== searchRequest.current) return;
      setSearchResults(mergeSearchResultsWithLoadedOrders(response.data.orders || [], loadedOrders));
      setSearchMessage(response.data.message || "");
      setIsSearchMode(true);
    } catch (error) {
      if (request !== searchRequest.current) return;
      console.error("搜索订单失败:", error);
      setSearchMessage("搜索失败，请重试");
      setSearchResults([]);
    } finally {
      if (request === searchRequest.current) setIsSearching(false);
    }
  };

  const clearSearch = () => {
    ++searchRequest.current;
    activeSearchKeyword.current = "";
    setIsSearching(false);
    setSearchKeyword("");
    setSearchResults([]);
    setSearchMessage("");
    setIsSearchMode(false);
  };

  const refreshSearchResults = (loadedOrders) => {
    if (activeSearchKeyword.current) {
      return handleSearchOrders(activeSearchKeyword.current, loadedOrders);
    }
  };

  const handleSearchKeyDown = (e) => {
    if (e.key === "Enter") {
      handleSearchOrders();
    }
  };

  const handleBulkMarkPaid = async () => {
    if (markingPaid.current || !selectedOrderIds.length) return;
    const ids = [...selectedOrderIds];
    if (!window.confirm(`确认已实际收到所选 ${ids.length} 笔订单的款项，并批量标记已付款？\n这会按现有“已收款”规则更新账务。已付款、取消或退款订单将跳过。`)) return;
    markingPaid.current = true;
    setIsMarkingPaid(true);
    try {
      const result = await markOrdersPaid(apiBaseUrl, ids);
      const remaining = new Set(result.failed.map(item => item.id));
      setSelectedOrderIds(current => current.filter(id => !ids.includes(id) || remaining.has(id)));
      setSelectAll(false);
      showPaymentNotice([`已标记 ${result.paid.length} 单，跳过 ${result.skipped.length} 单，失败 ${result.failed.length} 单`, ...result.skipped, ...result.failed.map(item => item.message)].join("\n"));
      try {
        await fetchOrders();
        await refreshSearchResults([]);
        refreshStats?.();
      } catch {
        showPaymentNotice("收款操作已完成，但刷新失败，请刷新页面核对结果。");
      }
    } finally {
      markingPaid.current = false;
      setIsMarkingPaid(false);
    }
  };

  const handleBulkDelete = async () => {
    if (selectedOrderIds.length === 0) {
      alert("请先选择要删除的订单");
      return;
    }

    const selectedIdSet = new Set(selectedOrderIds);
    const selectedOrders = orders.filter((order) => selectedIdSet.has(order.id));
    let confirmationText = null;
    if (containsPaidAdminOrder(selectedOrders)) {
      confirmationText = promptForPaidOrderDeletion(selectedOrderIds.length);
      if (!confirmationText) return;
    } else if (!window.confirm(`确定要删除选中的 ${selectedOrderIds.length} 个订单吗？此操作不可撤销。`)) {
      return;
    }

    setLoading(true);
    try {
      const response = await axios.post(`${apiBaseUrl}/admin/orders/bulk-delete`, {
        order_ids: selectedOrderIds,
        confirmation_text: confirmationText,
      });
      alert(response.data.message);
      setSelectedOrderIds([]);
      setSelectAll(false);
      refreshOrdersAndStats();
    } catch (error) {
      alert(getErrorMessage(error, "批量删除失败"));
    } finally {
      setLoading(false);
    }
  };

  const generateBookingPaymentPage = async (orderIdsOverride = null) => {
    const orderIds = Array.isArray(orderIdsOverride) ? orderIdsOverride.filter(Boolean) : selectedOrderIds;
    if (orderIds.length === 0) {
      showPaymentNotice("请先选择要生成付款页的订单");
      return;
    }

    setLoading(true);
    try {
      const response = await axios.post(`${apiBaseUrl}/admin/booking-payment-pages`, {
        order_ids: orderIds
      });
      const paymentUrl = response.data?.payment_page_url;
      const paymentMessage = paymentUrl ? formatBookingPaymentLinkMessage(paymentUrl) : "";
      const copied = paymentUrl ? await copyTextToClipboard(paymentMessage) : false;
      if (paymentUrl) {
        if (copied) {
          showPaymentNotice("付款文字已复制，可以直接粘贴发送给客户。");
        } else {
          setPaymentCopyPanel({
            paymentUrl,
            paymentMessage,
            copied,
            statusText: "复制未完成，请点“复制这段文字”或长按文字复制。",
          });
        }
      } else {
        showPaymentNotice("付款详情页已生成，但链接为空，请刷新后重试。");
      }
      setSelectedOrderIds([]);
      setSelectAll(false);
      refreshOrdersAndStats();
    } catch (error) {
      showPaymentNotice(getErrorMessage(error, "生成付款页失败"));
    } finally {
      setLoading(false);
    }
  };

  const closePaymentCopyPanel = () => {
    setPaymentCopyPanel(null);
  };

  const retryCopyPaymentMessage = async () => {
    if (!paymentCopyPanel?.paymentMessage) return;
    const copied = await copyTextToClipboard(paymentCopyPanel.paymentMessage);
    if (copied) {
      showPaymentNotice("付款文字已复制，可以直接粘贴发送给客户。");
      setPaymentCopyPanel(null);
      return;
    }
    setPaymentCopyPanel((current) => current ? {
      ...current,
      copied,
      statusText: "仍然无法复制，请长按文字复制，或使用 WhatsApp 发送。",
    } : current);
  };

  const openPaymentMessageWhatsApp = () => {
    if (!paymentCopyPanel?.paymentMessage || typeof window === "undefined") return;
    const whatsappUrl = `https://wa.me/?text=${encodeURIComponent(paymentCopyPanel.paymentMessage)}`;
    window.location.href = whatsappUrl;
  };

  const sharePaymentMessage = async () => {
    if (!paymentCopyPanel?.paymentMessage || typeof window === "undefined") return;
    if (navigator.share) {
      try {
        await navigator.share({
          title: "Booking payment link",
          text: paymentCopyPanel.paymentMessage,
        });
        return;
      } catch {
        // If the native share sheet is cancelled or unavailable, keep the WhatsApp fallback.
      }
    }
    openPaymentMessageWhatsApp();
  };

  const toggleSelectOrder = (orderId) => {
    setSelectedOrderIds(prev =>
      prev.includes(orderId)
        ? prev.filter(id => id !== orderId)
        : [...prev, orderId]
    );
  };

  const selectOrderGroup = (groupId) => {
    if (!groupId) return;
    const groupOrderIds = [...orders, ...searchResults]
      .filter(order => order.booking_group_id === groupId)
      .map(order => order.id)
      .filter(Boolean);
    setSelectedOrderIds(prev => Array.from(new Set([...prev, ...groupOrderIds])));
  };

  const toggleSelectAll = () => {
    if (selectAll) {
      setSelectedOrderIds([]);
    } else {
      setSelectedOrderIds(getFilteredOrders().map(o => o.id));
    }
    setSelectAll(!selectAll);
  };

  const resetFilters = () => {
      setServiceDateRange({ from: '', to: '' });
      setMonthFilter(getInitialMonthFilter());
      setCustomerFilter("");
      setUnlinkedCustomerOnly(false);
      setSortByDate(true);
      setDateSortDirection("asc");
      setHideCompleted(false);
      setHideCancelled(false);
      setOrderStatusFilter("all");
    };

    const cycleDateSort = () => {
      setSelectedOrderIds([]);
      setSelectAll(false);
      if (!sortByDate) {
        setSortByDate(true);
        setDateSortDirection("asc");
        return;
      }
      if (dateSortDirection === "asc") {
        setDateSortDirection("desc");
        return;
      }
      setSortByDate(false);
      setDateSortDirection("asc");
    };

  const toggleUnlinkedCustomerOnly = () => {
    const nextValue = !unlinkedCustomerOnly;
    setUnlinkedCustomerOnly(nextValue);
    setSelectedOrderIds([]);
    setSelectAll(false);
    if (nextValue) {
      setCustomerFilter("");
      if (!monthFilter) {
        setMonthFilter(getCurrentMonthValue());
      }
    }
  };

  const getTimeCategoryBadge = (order) => {
    const category = getOrderTimeCategory(order);
    const categoryMap = {
      completed: { text: "已完成", color: "premium-chip" },
      in_progress: { text: "进行中", color: "premium-chip-warning" },
      not_started: { text: "未开始", color: "premium-chip-success" },
    };
    const badge = categoryMap[category] || { text: "未知", color: "premium-chip" };
    return (
      <span className={badge.color}>
        {badge.text}
      </span>
    );
  };

  const getCharterTimeStatus = (order) => {
    try {
      return getCharterTimeReportStatus(order);
    } catch (e) {
      console.error("计算包车时间状态出错:", e);
      return null;
    }
  };

  const handleMarkOvertimeHandled = async (orderId) => {
    try {
      await axios.put(`${apiBaseUrl}/orders/${orderId}/mark-overtime-handled`);
      refreshOrdersAndStats();
    } catch (error) {
      alert("标记失败: " + (error.response?.data?.detail || error.message));
    }
  };

  const handleApplyCustomerOvertimeCharge = async (orderId) => {
    try {
      const previewResponse = await axios.get(`${apiBaseUrl}/orders/${orderId}/customer-overtime-charge-preview`);
      const suggestedFee = Number(previewResponse.data?.suggested_fee || 0);
      const currentFee = Number(previewResponse.data?.current_fee || 0);
      const chargeApplied = previewResponse.data?.charge_applied === true;
      const billedHours = Number(previewResponse.data?.billed_hours || 0);
      const amountText = window.prompt(
        `延长 ${billedHours} 小时。请输入${chargeApplied ? "修改后的" : "加入客户账单的"}超时费金额（日元）：\n系统建议：¥${suggestedFee.toLocaleString()}`,
        String(chargeApplied ? currentFee : suggestedFee)
      );
      if (amountText === null) return;
      const normalizedAmount = String(amountText).replace(/[¥￥,，\s]/g, "");
      if (!/^\d+$/.test(normalizedAmount) || Number(normalizedAmount) <= 0) {
        alert("请输入大于 0 的整数金额");
        return;
      }
      const enteredFee = Number(normalizedAmount);
      const requestBody = !chargeApplied && enteredFee === suggestedFee ? {} : { amount: enteredFee };
      const response = await axios.put(
        `${apiBaseUrl}/orders/${orderId}/apply-customer-overtime-charge`,
        requestBody
      );
      const overtimeFee = Number(response.data?.overtime_fee || 0).toLocaleString();
      const newTotal = Number(response.data?.new_total || 0).toLocaleString();
      const resultMessage = response.data?.base_payment_received
        ? response.data?.message
        : `延長料金 ${billedHours}小时 ¥${overtimeFee} ${chargeApplied ? "已修改" : "已加入客户账单"}`;
      alert(`${resultMessage}\n订单总额：¥${newTotal}`);
      refreshOrdersAndStats();
    } catch (error) {
      alert("添加超时费失败: " + (error.response?.data?.detail || error.message));
    }
  };

  const confirmRetailBooking = async (orderId) => {
    setLoading(true);
    try {
      const response = await axios.post(`${apiBaseUrl}/admin/public-booking/${orderId}/confirm`);
      const paymentUrl = response.data?.payment_url;
      const paymentStatus = response.data?.payment_status;
      alert(paymentUrl
        ? formatBookingPaymentLinkMessage(paymentUrl)
        : `Square未配置或付款链接生成失败。订单已确认，但付款链接未生成，请先配置 Square 或手动处理付款。\n状态：${paymentStatus || "unknown"}`);
      refreshOrdersAndStats();
    } catch (error) {
      alert(getErrorMessage(error, "散客订单确认失败"));
    } finally {
      setLoading(false);
    }
  };

  const cancelRetailBooking = async (order) => {
    const reason = window.prompt("请输入取消原因");
    if (!reason) return;
    const defaultAmount = order.total_price || order.price || "";
    const amountText = window.prompt("退款金额（日元）。未付款订单可留空。", defaultAmount ? String(defaultAmount) : "");
    const internal_note = window.prompt("内部备注（可选）") || "";
    setLoading(true);
    try {
      await axios.post(`${apiBaseUrl}/admin/public-booking/${order.id}/cancel`, {
        reason,
        refund_amount: amountText ? Number(amountText) : null,
        internal_note,
      });
      alert("取消/退款处理已记录");
      refreshOrdersAndStats();
    } catch (error) {
      alert(getErrorMessage(error, "散客订单取消失败"));
    } finally {
      setLoading(false);
    }
  };

  const groupOrdersByDate = (ordersToGroup, direction = dateSortDirection) => {
    const groups = {};
    const weekdayNames = ["星期日", "星期一", "星期二", "星期三", "星期四", "星期五", "星期六"];

    ordersToGroup.forEach(order => {
      const dateKey = order.departure_date || "未设定日期";
      if (!groups[dateKey]) {
        let dateLabel = dateKey;
        if (dateKey !== "未设定日期") {
          const date = new Date(dateKey);
          const month = date.getMonth() + 1;
          const day = date.getDate();
          const weekday = weekdayNames[date.getDay()];
          dateLabel = `${month}月${day}日 ${weekday}`;
        }
        groups[dateKey] = {
          date: dateKey,
          label: dateLabel,
          orders: []
        };
      }
      groups[dateKey].orders.push(order);
    });

    const sortedKeys = Object.keys(groups).sort((a, b) => {
      if (a === "未设定日期") return 1;
      if (b === "未设定日期") return -1;
      return direction === "desc" ? new Date(b) - new Date(a) : new Date(a) - new Date(b);
    });

    return sortedKeys.map(key => groups[key]);
  };

  const getStatusBadge = (status) => {
    const statusMap = {
      pending: { text: "待确认", color: "premium-chip-warning" },
      pending_payment: { text: "等待付款", color: "premium-chip-warning" },
      pending_admin_confirmation: { text: "散客待确认", color: "premium-chip-warning" },
      pending_quote: { text: "等待报价", color: "premium-chip" },
      quoted: { text: "已报价", color: "premium-chip" },
      confirmed: { text: "已确认", color: "premium-chip-success" },
      completed: { text: "已完成", color: "premium-chip" },
      cancelled: { text: "已取消", color: "premium-chip-destructive" },
      refund_processing: { text: "退款处理中", color: "premium-chip-warning" },
      refunded: { text: "已退款", color: "premium-chip" },
      refund_exception: { text: "退款异常", color: "premium-chip-destructive" },
    };

    const badge = statusMap[status] || {
      text: status,
      color: "premium-chip",
    };
    return (
      <span className={badge.color}>
        {badge.text}
      </span>
    );
  };

  const getVehicleTypeName = (type) => {
    const map = {
      alphard: "阿尔法",
      hiace: "海狮",
      premium_hiace: "豪华海狮",
      coaster: "考斯特",
      bus: "巴士",
      hiace_14_seats: "14座海狮",
      alpha: "阿尔法",
      luxury_hiace: "豪华海狮",
    };
    return map[type] || type;
  };

  const filteredOrders = getFilteredOrders();

  return {
    clearSearch,
    cancelRetailBooking,
    confirmRetailBooking,
    customerFilter,
    cycleDateSort,
    dateSortDirection,
    filteredOrders,
    getCharterTimeStatus,
    getFilteredOrders,
    getOrderTimeCategory,
    isDriverOvertimeUnresolvedOrder,
    isMissingDriverOrder,
    getStatusBadge,
    getTimeCategoryBadge,
    getUniqueCustomers,
    getVehicleTypeName,
    generateBookingPaymentPage,
    groupOrdersByDate,
    handleBulkDelete,
    handleBulkMarkPaid,
    isMarkingPaid,
    handleApplyCustomerOvertimeCharge,
    handleMarkOvertimeHandled,
    handleSearchKeyDown,
    handleSearchOrders,
    refreshSearchResults,
    hideCancelled,
    hideCompleted,
    isSearchMode,
    isSearching,
    monthFilter,
    serviceDateRange,
    setServiceDateRange: (range) => {
      setServiceDateRange(range);
      setSelectedOrderIds([]);
      setSelectAll(false);
    },
    orderStatusFilter,
    paymentCopyPanel,
    resetFilters,
    searchKeyword,
    searchMessage,
    searchResults: sortByDate ? sortOrdersByDeparture(searchResults, dateSortDirection) : searchResults,
    selectAll,
    selectedOrderIds,
    setCustomerFilter,
    setHideCancelled,
    setHideCompleted,
    setMonthFilter,
    setOrderStatusFilter,
    setSearchKeyword,
    setSortByDate,
    setDateSortDirection,
    sortByDate,
    toggleUnlinkedCustomerOnly,
    toggleSelectAll,
    toggleSelectOrder,
    selectOrderGroup,
    unlinkedCustomerOnly,
    closePaymentCopyPanel,
    retryCopyPaymentMessage,
    openPaymentMessageWhatsApp,
    sharePaymentMessage,
  };
}
